import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { difference } from 'ramda';

/* i18n */
import { useIntl } from 'react-intl';

/* Styles */
import styles from 'styles/recommendations-list.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import filterStyles from 'styles/filter-input.module.scss';
import ratingsStyles from 'styles/ratings.module.scss';

/* Assets */
import arrowDown from 'assets/images/Arrow_Dropdown.svg';
import thumbsUp from 'assets/images/ThumbsUp_Small_FilledBlack.svg';
import thumbsDown from 'assets/images/ThumbsDown_Small_FilledBlack.svg';
import thumbsUpGray from 'assets/images/ThumbsUp_Small_FilledGray.svg';
import thumbsDownGray from 'assets/images/ThumbsDown_Small_FilledGray.svg';
import thumbsUpFilled from 'assets/images/ThumbsUp_Small_Filled.svg';
import thumbsDownFilled from 'assets/images/ThumbsDown_Small_Filled.svg';

/* API */
import { rateItem } from 'lib/API';

/* Redux */
import { useSelector, useDispatch } from 'react-redux';
import { setActiveSoc, setPage as setJobsPage } from 'app/slices/jobsSlice';
import {
  setCurrentTab,
  setActiveFilters,
  thumbsUpClick,
  thumbsDownClick,
  revertThumbsClick
} from 'app/slices/ratingsSlice';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import TimeMe from "timeme.js";

/* Material UI and other UI Dependencies */
import Container from '@material-ui/core/Container';
import Typography from "@material-ui/core/Typography";
import withWidth from '@material-ui/core/withWidth';
import Box from '@material-ui/core/Box';
import RecommendationsCard from 'components/RecommendationsCard';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';

/* UI Components */
import Tag from "components/Tag";
import SkillCard from 'components/SkillCard';
import { useSnackbar } from 'notistack';

const Ratings =({locale, width}) => {

  const [sortBy, setSortBy] = useState('NEWEST');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortingOpen, setSortingOpen] = useState(false);
  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;

  const dispatch = useDispatch();
  let history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const sortingOptions = [
    {value: 'NEWEST', title: intl.formatMessage({id:'ratings.sort.newest', description:'string'})},
    {value: 'OLDEST', title: intl.formatMessage({id:'ratings.sort.oldest', description:'string'})},
  ];
  
  const {
    currentTab,
    results,
    availableFilters,
    activeFilters
  } = useSelector((state) => state.ratings);

  const translatedAvailableFilters = availableFilters.map(f => ({
    title: (intl.formatMessage(
      {id: `ratings.filters.${f}`, description:'string'}
    )),
    value: f
  }));
  const translatedFiltersDict = availableFilters.reduce((prev, curr) => {
    return {...prev, [curr]: (intl.formatMessage({id: `ratings.filters.${curr}`}))};
  }, {});

  const showResults = results[currentTab];
  const sortedshowResults = showResults.slice().sort(({ date: a }, { date: b }) => {
    return sortBy === 'NEWEST' ? b - a : a - b;
  });

  const filteredResuls = results[currentTab].filter(r => (
      activeFilters.indexOf(r.type) > -1)
    );
  const sortedFilteredResults = filteredResuls.slice().sort(({ date: a }, { date: b }) => {
    return sortBy === 'NEWEST' ? b - a : a - b;
  });

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const smallScreen = ['xs'].indexOf(width) > -1;

  const removeFilter = (tagId) => (e) => {
    e.preventDefault();
    const newActiveFilters = activeFilters.filter(f => f !== tagId);
    dispatch(setActiveFilters(newActiveFilters));
  }

  const clearAllFilters = () => {
    dispatch(setActiveFilters([]));
  }

  const showJobs = ({ soc_code }) => {
    dispatch(setActiveSoc(soc_code));
    dispatch(setJobsPage(0));
    history.push(`${urlPrefix}/jobs`);
  }

  const handleButtonClick = (item) => () => {
    const { type } = item;
    const actionDict = {
      career: 'RATINGS_CAREER_JOBS',
      job: 'RATINGS_JOB_APPLY',
      training: 'RATINGS_TRAINING_APPLY',
    }
    trackEvent(actionDict[type], item);
    // Show jobs when clicking on a careers' card
    if (type === 'career') {
      showJobs(item);
    }
  }

  const handleTagClick = (item) => () => {
    const { type } = item;
    if (type === 'career') {
      const eventName = 'RATINGS_CAREER_CHICLET';
      trackEvent(eventName, item);
      //TODO: Expand drawer here
    }
    
  }

  const handleLikeClick = (item) => () => {
    const { type } = item;
    const originalItem = { ...item };
    const updatedItem = { ...item, liked: !originalItem.liked, disliked: false };

    dispatch(thumbsUpClick({ ...updatedItem, type }));
    rateItem(item.id, 'liked', item, originalItem.liked).catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...originalItem, type }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    })

    if (updatedItem.liked) {
      const eventName = `RATINGS_${type.toUpperCase()}_THUMBSUP`;
      trackEvent(eventName, item);
    }
  }

  const handleDislikeClick = (item) => () => {
    const { type } = item;
    const originalItem = { ...item };
    const updatedItem = { ...item, liked: false, disliked: !originalItem.disliked };

    dispatch(thumbsDownClick({ ...updatedItem, type }));
    rateItem(item.id, 'disliked', item, originalItem.disliked).catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...originalItem, type }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    })

    if (updatedItem.disliked) {
      const eventName = `RATINGS_${type.toUpperCase()}_THUMBSDOWN`;
      trackEvent(eventName, item);
    }
  }

  const handleThumbsUpTabClick = () => {
    const eventName = 'RATINGS_TOGGLE_THUMBSUP';
    trackEvent(eventName);
    dispatch(setCurrentTab('up'))
  }

  const handleThumbsDownTabClick = () => {
    const eventName = 'RATINGS_TOGGLE_THUMBSDOWN';
    trackEvent(eventName);
    dispatch(setCurrentTab('down'))
  }

  const handleFiltersChange = ({ target: { value: newValue } }) => {
    const filterDict = {
      career: 'RATINGS_FILTER_CAREERS',
      job: 'RATINGS_FILTER_JOBS',
      training: 'RATINGS_FILTER_TRAINING',
      skill: 'RATINGS_FILTER_SKILLS',
    }
    const filtersDifference = difference(newValue, activeFilters);
    if (filtersDifference.length > 0) {
      trackEvent(filterDict[filtersDifference[0]]);
    }
    dispatch(setActiveFilters(newValue))
  }

  const handleSortingChange = ({target: { value }}) => {
    setSortBy(value);
    trackEvent(`RATINGS_SORT_${value}`);
  }

  useEffect(() => {
    TimeMe.stopTimer();
    TimeMe.setCurrentPageName("RATINGS");
    TimeMe.startTimer();
    return () => {
      console.log('Leaving Ratings')
    };
  }, []);

  return(
    <>
      <div className={styles.content}>
        <Container maxWidth="lg">
          <div>
            <Typography variant="h1" component="h2">
              {intl.formatMessage(
                {id: currentTab === 'up' ? 'ratings.thumbsUpTitle' : 'ratings.thumbsDownTitle', description:'string'}
              )}
            </Typography>
          </div>
          <div className={ratingsStyles.likesFilters}>
            <div>
              <span className={ratingsStyles.thumbsButton} onClick={handleThumbsUpTabClick}>
                <img alt="Thumbs Up" src={currentTab === 'up' ? thumbsUp : thumbsUpGray} className={cn(ratingsStyles.thumbsImage, ratingsStyles.defaultThumbs)} />
                <img alt="Thumbs Up" src={thumbsUpFilled} className={cn(ratingsStyles.thumbsImage, ratingsStyles.filledThumbs)} />
              </span>
              <span className={ratingsStyles.thumbsButton} onClick={handleThumbsDownTabClick}>
                <img alt="Thumbs Down" src={currentTab === 'down' ? thumbsDown : thumbsDownGray} className={cn(ratingsStyles.thumbsImage, ratingsStyles.defaultThumbs)} />
                <img alt="Thumbs Down" src={thumbsDownFilled} className={cn(ratingsStyles.thumbsImage, ratingsStyles.filledThumbs)} />
              </span>
            </div>
            <div className={ratingsStyles.filters}>
              {sortedshowResults.length > 0 && (
                <>
                  <FormControl
                    className={cn({
                      [filterStyles.formControl]: true,
                      [filterStyles.ratingsSortingFocused]: sortingOpen
                    })}
                  >
                    <span className={filterStyles.selectLabel}>
                      {intl.formatMessage({id: sortBy === 'NEWEST' ? 'ratings.sort.newest' : 'ratings.sort.oldest'})}
                    </span>
                    <Select
                      disableUnderline
                      disabled={showResults.length === 0}
                      labelId="sort-select-label"
                      id="sort-select"
                      value={sortBy}
                      onChange={handleSortingChange}
                      onOpen={() => setSortingOpen(true)}
                      onClose={() => setSortingOpen(false)}
                      input={<Input />}
                      renderValue={() => ''}
                      IconComponent={(_props) => <img alt="Arrow down" className={filterStyles.arrowDown} src={arrowDown} />}
                      MenuProps={{
                        PaperProps: {
                          className: filterStyles.selectMenu,
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left"
                        },
                        getContentAnchorEl: null
                      }}
                      classes={{
                        root: filterStyles.selectRoot,
                        select: filterStyles.selectSelect,
                      }}
                    >
                      {sortingOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <span className={filterStyles.checkboxLabel}>
                            {option.title}
                          </span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl
                    className={cn({
                      [filterStyles.formControl]: true,
                      [filterStyles.ratingsFiltersFocused]: filtersOpen
                    })}
                  >
                    <span className={filterStyles.selectLabel}>
                      {intl.formatMessage({id:'ratings.filter', description:'string'})}
                    </span>
                    <Select
                      multiple
                      disableUnderline
                      disabled={showResults.length === 0}
                      labelId="filters-select-label"
                      id="filters-select"
                      value={activeFilters}
                      onChange={handleFiltersChange}
                      onOpen={() => setFiltersOpen(true)}
                      onClose={() => setFiltersOpen(false)}
                      input={<Input />}
                      renderValue={() => ''}
                      IconComponent={(_props) => <img alt="Arrow down" className={filterStyles.arrowDown} src={arrowDown} />}
                      MenuProps={{
                        PaperProps: {
                          className: filterStyles.selectMenu,
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left"
                        },
                        getContentAnchorEl: null
                      }}
                      classes={{
                        root: filterStyles.selectRoot,
                        select: filterStyles.selectSelect,
                      }}
                    >
                      {translatedAvailableFilters.map((filter) => (
                        <MenuItem key={filter.value} value={filter.value}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            classes={{
                              root: filterStyles.checkboxRoot
                            }}
                            style={{ marginRight: -3, marginLeft: 0, padding: '5px 9px 7px 4px' }}
                            checked={activeFilters.indexOf(filter.value) > -1}
                          />
                          <span className={filterStyles.checkboxLabel}>
                            {filter.title}
                          </span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </div>
          </div>
          <hr className={layoutStyles.separator} />
          <Box m={4} />
            {activeFilters.length === 0 && sortedshowResults.length > 0 && sortedshowResults.map(item => (
              <>
                <Box m={3} />
                {item.type === 'skill' ? (
                  <SkillCard
                    item={item}
                    rating={results.lookup[item.id]}
                    align="left"
                    onLikeClick={handleLikeClick(item)}
                    onDislikeClick={handleDislikeClick(item)}
                  />
                ) : (
                  <RecommendationsCard
                    type={item.type}
                    item={item}
                    link={item.type === 'job' ? item.apply : (item.type === 'training' ? item.link : undefined)}
                    rating={results.lookup[item.id]}
                    onButtonClick={handleButtonClick(item)}
                    onLikeClick={handleLikeClick(item)}
                    onDislikeClick={handleDislikeClick(item)}
                    onTagClick={handleTagClick(item)}
                  />
                )}
              </>
            ))}
            {
              sortedshowResults.length === 0 && (
                <>
                <Box m={3} />
                <Box align="center" className={ratingsStyles.emptyStateContainer}>
                  <Typography variant="h3" gutterBottom>
                    {intl.formatMessage(
                      {id: `ratings.${currentTab === 'up' ? 'thumbsUpEmptyStateTitle' : 'thumbsDownEmptyStateTitle'}`, description: 'string'}
                    )}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {intl.formatMessage(
                      {id: `ratings.${currentTab === 'up' ? 'thumbsUpEmptyStateText' : 'thumbsDownEmptyStateText'}`, description: 'string'}
                    )}
                  </Typography>
                </Box>
              </>
              )
            }
            {activeFilters.length > 0 && (
              <>
                <Typography variant="h3" gutterBottom align={smallScreen ? 'center' : 'inherit'}>
                  {intl.formatMessage(
                    {id:'jobs.filterResultsTitle', description:'string'}
                  )}
                </Typography>
                <div className={filterStyles.tags}>
                  <Typography variant="h5">
                    {intl.formatMessage(
                      {id:'recommendations.filterBy', description:'string'}
                    )}
                  </Typography>
                  {activeFilters.map(f => (
                    <Tag text={translatedFiltersDict[f]} onClick={removeFilter(f)} type="filter" />
                  ))}
                  <span
                    className={cn(layoutStyles.link, filterStyles.clearAll)}
                    onClick={clearAllFilters}
                  >
                      {intl.formatMessage({id:'recommendations.clearAllFilters', description:'string'})}
                  </span>
                </div>
                <Box m={6} />
                {sortedFilteredResults.length === 0
                  ? (
                    <Typography variant="body2">
                      {intl.formatMessage(
                        {id:'ratings.noRatings'}
                      )}
                    </Typography>
                  ) : null
                }
                {sortedFilteredResults.map(item => (
                  <>
                    <Box m={3} />
                    {item.type === 'experience' ? (
                      <SkillCard
                        item={item}
                        align="left"
                        onLikeClick={handleLikeClick(item)}
                        onDislikeClick={handleDislikeClick(item)}
                      />
                    ) : (
                      <RecommendationsCard
                        type={item.type}
                        item={item}
                        onButtonClick={handleButtonClick(item)}
                        onLikeClick={handleLikeClick(item)}
                        onDislikeClick={handleDislikeClick(item)}
                        onTagClick={handleTagClick(item)}
                      />
                    )}
                  </>
                ))}
              </>
            )}
        </Container>
      </div>
    </>
  )
}

export default withWidth()(Ratings);